import { useEffect } from "react";

/**
 * Push a synthetic history entry when an overlay (dialog/modal) opens, so the
 * Android/iOS hardware back button closes the overlay instead of navigating
 * the underlying page away.
 *
 * Design notes:
 *  - We do NOT call history.back() on cleanup, because that interacts badly
 *    with React 18 StrictMode (double-invoke of effects in dev) and could
 *    cause spurious popstate events that close the overlay right after it
 *    opens, which manifested as: clicking a rental mode flashing the modal
 *    and bouncing back to /passenger.
 *  - We deduplicate by checking history.state to avoid stacking entries on
 *    rapid re-mounts.
 *  - The trade-off is one extra (silent, same-URL) history entry per
 *    UI-driven close — acceptable; the very next hardware-back press just
 *    consumes it and the user stays on the same page.
 */
export default function useBackButtonClose(open, onOpenChange) {
  useEffect(() => {
    if (!open) return undefined;
    if (typeof window === "undefined") return undefined;

    // Avoid pushing twice for the same open transition (StrictMode safe).
    const current = window.history.state;
    const alreadyPushed = current && current.__overlayOpen === true;
    if (!alreadyPushed) {
      try {
        window.history.pushState({ __overlayOpen: true, ts: Date.now() }, "");
      } catch (_) {
        return undefined;
      }
    }

    const onPopState = () => {
      // Back button pressed -> close overlay instead of leaving page
      if (typeof onOpenChange === "function") onOpenChange(false);
    };
    window.addEventListener("popstate", onPopState);

    return () => {
      window.removeEventListener("popstate", onPopState);
      // Intentionally NO history.back() here.
    };
  }, [open, onOpenChange]);
}
